import PropTypes from 'prop-types';
import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Button,
  CircularProgress,
  Pagination,
  Tooltip,
} from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Document, Page, pdfjs } from 'react-pdf';
import Iconify from '../iconify';
import Lightbox from '../lightbox';

// Set up PDF.js worker - serve from public folder to avoid CORS issues
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
// ----------------------------------------------------------------------

DocumentViewer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  documentUrl: PropTypes.string,
  documentName: PropTypes.string,
};

export default function DocumentViewer({ open, onClose, documentUrl, documentName = 'Document' }) {
  const isImage = documentUrl && /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(documentUrl);
  const isPdf = documentUrl && /\.pdf$/i.test(documentUrl);

  const [loading, setLoading] = useState(() => isImage); // Only show loading for images
  const [error, setError] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const handleLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = documentName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onPdfLoadSuccess = ({ numPages: nextNumPages }) => {
    setNumPages(nextNumPages);
    setPdfLoading(false);
    setError(false);
  };

  const onPdfLoadError = (pdfError) => {
    console.error('Error loading PDF:', pdfError);
    setPdfLoading(false);
    setError(true);
  };

  const onPdfLoadStart = () => {
    setPdfLoading(true);
    setError(false);
  };

  const changePage = (event, value) => {
    setPageNumber(value);
  };

  const handleOpenLightbox = () => {
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
        }}
      >
        <Typography variant="h6" component="div">
          {documentName}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Iconify icon="eva:close-fill" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200,
              gap: 2,
            }}
          >
            <Iconify icon="eva:alert-circle-fill" sx={{ fontSize: 48, color: 'error.main' }} />
            <Typography variant="body1" color="error">
              Errore nel caricamento del documento
            </Typography>
            <Button
              variant="outlined"
              onClick={handleDownload}
              startIcon={<Iconify icon="ic:baseline-download" />}
            >
              Scarica invece
            </Button>
          </Box>
        )}

        {!error && documentUrl && (
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {(() => {
              if (isImage) {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      p: 2,
                      position: 'relative',
                    }}
                  >
                    <Box
                      component="button"
                      onClick={handleOpenLightbox}
                      sx={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'zoom-in',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                      }}
                      aria-label="Clicca per ingrandire l'immagine"
                    >
                      <img
                        src={documentUrl}
                        alt={documentName}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                          pointerEvents: 'none',
                        }}
                        onLoad={handleLoad}
                        onError={handleError}
                      />
                    </Box>

                    {/* Zoom button overlay */}
                    <Tooltip title="Ingrandisci immagine" placement="left">
                      <IconButton
                        onClick={handleOpenLightbox}
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          },
                          zIndex: 1,
                        }}
                        size="small"
                      >
                        <Iconify icon="eva:zoom-in-fill" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              }

              if (isPdf) {
                return (
                  <Box
                    sx={{
                      height: '100%',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      overflow: 'auto',
                    }}
                  >
                    {pdfLoading && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: 200,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    )}

                    <Document
                      file={documentUrl}
                      onLoadSuccess={onPdfLoadSuccess}
                      onLoadError={onPdfLoadError}
                      onLoadStart={onPdfLoadStart}
                      loading={<CircularProgress />}
                      error={
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            p: 3,
                          }}
                        >
                          <Iconify
                            icon="eva:alert-circle-fill"
                            sx={{ fontSize: 48, color: 'error.main' }}
                          />
                          <Typography variant="body1" color="error">
                            Errore nel caricamento del PDF
                          </Typography>
                          <Button
                            variant="outlined"
                            onClick={handleDownload}
                            startIcon={<Iconify icon="ic:baseline-download" />}
                          >
                            Scarica invece
                          </Button>
                        </Box>
                      }
                    >
                      <Page
                        pageNumber={pageNumber}
                        width={Math.min(800, window.innerWidth - 100)}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </Document>

                    {numPages > 1 && (
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Pagination
                          count={numPages}
                          page={pageNumber}
                          onChange={changePage}
                          color="primary"
                          showFirstButton
                          showLastButton
                        />
                        <Typography variant="body2" color="text.secondary">
                          Pagina {pageNumber} di {numPages}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              }

              return (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 200,
                    gap: 2,
                  }}
                >
                  <Iconify icon="eva:file-text-fill" sx={{ fontSize: 48, color: 'primary.main' }} />
                  <Typography variant="body1">
                    Anteprima non disponibile per questo tipo di file
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={handleDownload}
                    startIcon={<Iconify icon="ic:baseline-download" />}
                  >
                    Scarica per visualizzare
                  </Button>
                </Box>
              );
            })()}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="contained"
          startIcon={<Iconify icon="ic:baseline-download" />}
          onClick={handleDownload}
          disabled={!documentUrl}
        >
          Scarica
        </Button>
        <Button onClick={onClose}>Chiudi</Button>
      </DialogActions>

      {/* Lightbox for image zoom */}
      <Lightbox
        open={lightboxOpen}
        close={handleCloseLightbox}
        slides={[
          {
            src: documentUrl,
            alt: documentName,
            title: documentName,
          },
        ]}
        disabledVideo
        disabledCaptions={false}
      />
    </Dialog>
  );
}
